import { useEffect, useRef, useState } from 'react'
import { AsyncThunk } from '@reduxjs/toolkit'
import { useAppDispatch } from 'src/store/hooks'
import { Status } from 'src/store/store'

// Always re-fetches on mount (and whenever arg changes), and returns true only once that
// fetch has gone pending → fulfilled. Callers gate on it so cached Redux state for arg
// never decides what renders before the fresh response arrives.
//
// status must be the Redux status keyed by the current arg, and the slice must guard it by
// requestId so only the most recently dispatched request for arg can settle it (see
// latestProgressRequestID in events-v2.slice.ts). Without that, an overlapping request — a
// fetch still in flight from the previous page, or StrictMode's double mount — settling last
// would decide the status, and a rejected → fulfilled sequence would never mark this fresh.
//
// On an arg change the transition watch restarts from this arg's own status, so a previous
// arg's pending status followed by this arg's cached fulfilled one can't be mistaken for a
// fresh fetch completing, and an arg that is already pending when we switch to it still
// completes. doneFor is cleared on every dispatch and only counts while dispatchedFor matches
// arg, so a flag earned on an earlier visit (A → B → A) can't open the gate before the new
// request for A has even started.
export let useFreshFetch = (
  arg: string,
  status: Status | undefined,
  thunk: AsyncThunk<any, string, any>,
): boolean => {
  let dispatch = useAppDispatch()
  let [doneFor, setDoneFor] = useState<string | null>(null)
  let [dispatchedFor, setDispatchedFor] = useState<string | null>(null)
  let prevRef = useRef<{ arg: string; status: Status | undefined } | null>(null)

  useEffect(() => {
    setDoneFor(null)
    setDispatchedFor(arg)
    if (arg) dispatch(thunk(arg))
  }, [dispatch, arg])

  // On an arg change this only seeds the watch with the status this render saw for the new
  // arg; a transition counts only when both ends belong to the same arg.
  useEffect(() => {
    let prev = prevRef.current
    if (
      prev?.arg === arg &&
      prev.status === 'pending' &&
      status === 'fulfilled'
    )
      setDoneFor(arg)
    // eslint-disable-next-line fp/no-mutation
    prevRef.current = { arg, status }
  }, [status, arg])

  return !!arg && dispatchedFor === arg && doneFor === arg
}
